import type { QueryClient } from "@tanstack/react-query"
import type { AnyRouter } from "@tanstack/react-router"
import posthog from "posthog-js"
import type { WsClientMessage, WsServerMessage } from "../generated-social"
import { wsStore } from "./store"

export type SubscriptionHandler<T extends WsServerMessage["type"]> = (
	payload: Extract<WsServerMessage, { type: T }>,
	queryClient: QueryClient,
	router: AnyRouter,
) => void | Promise<void>

type MessageTypeToHandler = {
	[K in WsServerMessage["type"]]?: Set<SubscriptionHandler<K>>
}

class WebSocketBridge {
	private handlers: MessageTypeToHandler = {}
	private queryClient: QueryClient | null = null
	private router: AnyRouter | null = null
	private unsubscribe: (() => void) | null = null

	init(queryClient: QueryClient, router: AnyRouter): void {
		if (this.unsubscribe) {
			this.unsubscribe()
		}

		this.queryClient = queryClient
		this.router = router
		this.unsubscribe = wsStore.on("message", (payload) => {
			this.handleMessage(payload)
		})
	}

	subscribe<T extends WsServerMessage["type"]>(
		messageType: T,
		handler: SubscriptionHandler<T>,
	): () => void {
		if (!this.handlers[messageType]) {
			this.handlers[messageType] = new Set() as MessageTypeToHandler[T]
		}

		const handlersSet = this.handlers[messageType] as Set<
			SubscriptionHandler<T>
		>
		handlersSet.add(handler)

		return () => {
			handlersSet.delete(handler)
			if (handlersSet.size === 0) {
				delete this.handlers[messageType]
			}
		}
	}

	send(payload: WsClientMessage): boolean {
		return wsStore.send(payload)
	}

	getContext() {
		return {
			queryClient: this.queryClient,
			router: this.router,
		}
	}

	destroy(): void {
		this.unsubscribe?.()
		this.unsubscribe = null
		this.handlers = {}
		this.queryClient = null
		this.router = null
	}

	private handleMessage(payload: WsServerMessage): void {
		if (!this.queryClient || !this.router) return

		const handlersSet = this.handlers[payload.type]
		if (!handlersSet || handlersSet.size === 0) return

		for (const handler of handlersSet) {
			try {
				const result = handler(payload as never, this.queryClient, this.router)
				if (result) result.catch((error) => reportHandlerError(payload, error))
			} catch (error) {
				reportHandlerError(payload, error)
			}
		}
	}
}

function reportHandlerError(payload: WsServerMessage, error: unknown): void {
	console.error(`[WS Bridge] Handler error for ${payload.type}:`, error)
	const properties: Record<string, unknown> = {
		scope: `ws.handler.${payload.type}`,
	}
	if (payload.type === "notification") {
		properties.name = payload.name
		properties.notification_id = payload.notification_id
	}
	posthog.captureException(error, properties)
}

export const wsBridge = new WebSocketBridge()
